// Screen language. Each person's language is in their profile (users.language) and is changed ONLY with the language
// button at the top right of every screen (approved 19/10/2026: no other place, so nobody is confused). English is the
// default. The sign-in, two-factor and password screens stay English.
//  - The QC inspector screens use the keyed wording (t(), shared/i18n) and are wrapped with lang / dir (langWrap).
//  - Every other screen is translated on screen from the phrase files (screenlang.ts).
// Artwork, labels, CAD, documents and anything people type stay exactly as they are.
import { LANGS, LANG_ENGLISH_NAME, langButtonCode, langInfo, normalizeLang, translate } from "../../shared/i18n/index.js";
import { esc, put, toast } from "./lib.js";
import { onAction, state } from "./state.js";
import { icon } from "./icons.js";

const DEVICE_KEY = "falcon_lang";
export function deviceLang(): string { try { return normalizeLang(localStorage.getItem(DEVICE_KEY)); } catch { return "en"; } }
function rememberOnDevice(code: string) { try { localStorage.setItem(DEVICE_KEY, code); } catch { /* private window */ } }
/** The person's screen language once signed in; English before (the sign-in screens are English only). */
export function currentLang(): string {
  const s = state.session;
  return s?.signedIn && s.mfaPassed && s.user?.language ? normalizeLang(s.user.language) : "en";
}
/** After signing in this device remembers the language (only used to avoid a flash of English while loading). */
export function syncDeviceLang(): void { const u = state.session?.user?.language; if (state.session?.signedIn && state.session.mfaPassed && u) rememberOnDevice(normalizeLang(u)); }
export const t = (key: string, vars?: Record<string, string | number>) => translate(currentLang(), key, vars);
/** English wording whatever the language (sign-in screens). */
export const en = (key: string, vars?: Record<string, string | number>) => translate("en", key, vars);
export const isRtl = () => langInfo(currentLang()).dir === "rtl";
/** The QC inspector screens' content: English is returned exactly as it is; another language is wrapped with its
 *  lang / dir (Arabic and Urdu right to left), scoped to this content only. */
export function langWrap(html: string): string {
  const l = langInfo(currentLang());
  if (l.code === "en") return html;
  return `<div class="i18n i18n-${l.code}" lang="${l.code}" dir="${l.dir}">${html}</div>`;
}
/** Left to right inside right-to-left text: PO numbers, codes, colours, numbers, measurements. */
export const ltr = (text: string) => `<bdi dir="ltr">${esc(text)}</bdi>`;

// ---- The language button (top right of the header) and its list ------------------------------------------------------
/** The button: the language icon and the current language's short code, opening the list of languages. */
export function langButtonHtml(): string {
  const cur = currentLang();
  // Always shown in English as well as the language's own name so anyone can find their way back to English.
  const label = `Language · ${LANG_ENGLISH_NAME[cur] || cur}`;
  return `<div class="falcon-lang-wrap" data-no-tr>
  <button type="button" class="falcon-lang-btn" id="lang-button" data-action="toggle-lang-menu" aria-haspopup="listbox" aria-expanded="false" aria-controls="lang-menu" title="${esc(label)}" aria-label="${esc(label)}">${icon("language")}<span class="falcon-lang-code" lang="${esc(cur)}">${esc(langButtonCode(cur))}</span><span class="falcon-lang-chev" aria-hidden="true">▾</span></button>
  <div class="falcon-lang-menu" id="lang-menu" role="listbox" aria-label="Language" hidden>${LANGS.map((l) => `<button type="button" role="option" class="falcon-lang-opt ${l.code === cur ? "is-on" : ""}" aria-selected="${l.code === cur}" data-action="set-lang" data-lang="${esc(l.code)}"><span lang="${esc(l.code)}" dir="${l.dir}">${esc(l.name)}</span>${l.code === "en" ? "" : `<small>${esc(LANG_ENGLISH_NAME[l.code] || "")}</small>`}<i aria-hidden="true">${l.code === cur ? "✓" : ""}</i></button>`).join("")}</div>
</div>`;
}
export function closeLangMenu(): void {
  const m = document.getElementById("lang-menu");
  if (!m || m.hidden) return;
  m.hidden = true;
  document.getElementById("lang-button")?.setAttribute("aria-expanded", "false");
}
onAction("toggle-lang-menu", (el) => {
  const m = document.getElementById("lang-menu");
  if (!m) return;
  const open = m.hidden;
  m.hidden = !open;
  el.setAttribute("aria-expanded", String(open));
  if (open) m.querySelector<HTMLElement>(".is-on")?.focus();
});
document.addEventListener("click", (e) => { if (!(e.target as HTMLElement)?.closest?.(".falcon-lang-wrap")) closeLangMenu(); });
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && !document.getElementById("lang-menu")?.hidden) { closeLangMenu(); document.getElementById("lang-button")?.focus(); }
});
onAction("set-lang", async (el) => {
  const code = normalizeLang(el.dataset.lang);
  closeLangMenu();
  if (code === currentLang()) return;
  if (!(state.session?.signedIn && state.session.mfaPassed && state.session.user?.id)) return;
  try { await put("/api/account/language", { language: code }); }
  catch (err) { toast((err as Error).message, "error"); return; }
  rememberOnDevice(code);
  // A fresh start in the new language: every screen, list and pop-up is drawn again in it (and back to English
  // exactly as before when English is chosen).
  location.reload();
});
